import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { LayoutGrid } from "lucide-react";
import ProfileHeader from "@/components/ProfileHeader";
import SectionHeader from "@/components/SectionHeader";
import ListItem from "@/components/ListItem";
import GroupedListItem from "@/components/GroupedListItem";
import CurrentStatus from "@/components/CurrentStatus";
import InspirationCard from "@/components/InspirationCard";
import AppsSlotCard from "@/components/AppsSlotCard";
import CoinTransition from "@/components/CoinTransition";
import profilePhotoColor from "@/assets/profile-photo-color.webp";
import profilePhoto from "@/assets/profile-photo.webp";
import { workExperience, volunteering, education, inspirationThemes } from "@/data/portfolio";
import { useGallery } from "@/contexts/GalleryContext";

const MAX_ITEMS = 2;

const Index = () => {
  const [showHint, setShowHint] = useState(false);
  const [portraitAspect, setPortraitAspect] = useState(1.3);
  const [imagesReady, setImagesReady] = useState(false);
  const [transition, setTransition] = useState<{
    coinX: number;
    coinY: number;
    cardWidth: number;
    cardHeight: number;
  } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      }
    }
  }, [location.hash]);

  const toggleSection = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderSection = (
    title: string,
    key: string,
    items: typeof workExperience
  ) => {
    const hasMore = items.length > MAX_ITEMS;
    const isExpanded = expanded[key] || false;
    const visible = isExpanded ? items : items.slice(0, MAX_ITEMS);
    const extra = items.slice(MAX_ITEMS);

    return (
      <>
        <SectionHeader
          title={title}
          count={items.length}
          showExplore={hasMore}
          expanded={isExpanded}
          onToggle={() => toggleSection(key)}
        />
        {items.slice(0, MAX_ITEMS).map((item, i) =>
          item.roles ? (
            <GroupedListItem key={item.title} {...item} roles={item.roles} delay={0.1 * i} />
          ) : (
            <ListItem key={item.title} {...item} delay={0.1 * i} />
          )
        )}
        <AnimatePresence>
          {isExpanded &&
            extra.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, height: 0, y: -8 }}
                animate={{ opacity: 1, height: "auto", y: 0 }}
                exit={{ opacity: 0, height: 0, y: -8 }}
                transition={{
                  height: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.15 * i },
                  opacity: { duration: 0.3, ease: "easeOut", delay: 0.15 * i + 0.1 },
                  y: { duration: 0.3, ease: "easeOut", delay: 0.15 * i + 0.1 },
                }}
                style={{ overflow: "hidden" }}
              >
                {item.roles ? (
                  <GroupedListItem {...item} roles={item.roles} delay={0} />
                ) : (
                  <ListItem {...item} delay={0} />
                )}
              </motion.div>
            ))}
        </AnimatePresence>
      </>
    );
  };

  useEffect(() => {
    const img1 = new Image();
    const img2 = new Image();
    img1.src = profilePhotoColor;
    img2.src = profilePhoto;

    Promise.all([
      img1.decode().then(() => {
        if (img1.width > 0) setPortraitAspect(img1.height / img1.width);
      }),
      img2.decode(),
    ])
      .then(() => setImagesReady(true))
      .catch(() => setImagesReady(true)); // fallback so coin isn't permanently locked
  }, []);

  useEffect(() => {
    if (!transition) {
      setShowHint(false);
      const timer = setTimeout(() => setShowHint(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [transition]);

  const handleCoinClick = (coinRect: DOMRect) => {
    if (!imagesReady) return; // Don't start animation until images are decoded
    const card = cardRef.current?.getBoundingClientRect();
    setShowHint(false);
    setTransition({
      coinX: coinRect.left - card.left,
      coinY: coinRect.top - card.top,
      cardWidth: card.width,
      cardHeight: card.height,
    });
  };

  const handleBack = () => {
    setTransition(null);
  };

  const { items, clearAll } = useGallery();
  const galleryCount = items.length;

  return (
    <main className="flex min-h-screen items-start justify-center px-4 py-12 sm:py-20 relative">
      {/* Preload images so coin animation never shows blank faces */}
      <img src={profilePhotoColor} alt="" className="sr-only" aria-hidden="true" decoding="sync" />
      <img src={profilePhoto} alt="" className="sr-only" aria-hidden="true" decoding="sync" />
      {/* Floating Gallery badge */}
      <AnimatePresence>
        {galleryCount > 0 && !transition && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-2"
          >
            <Link
              to="/gallery"
              className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-lg transition-all hover:shadow-xl hover:scale-105"
            >
              <LayoutGrid className="h-4 w-4" />
              Gallery
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-foreground/20 text-xs">
                {galleryCount}
              </span>
            </Link>
            <button
              onClick={clearAll}
              className="text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              Reset gallery
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        ref={cardRef}
        className="w-full max-w-[500px] rounded-3xl bg-card relative overflow-hidden"
      >
        {/* Landing content — hidden (not unmounted) during transition to prevent flash */}
        <div className="p-6 sm:p-8" style={{ visibility: transition ? 'hidden' : 'visible', position: transition ? 'absolute' : 'relative', inset: 0 }}>

          <div className="anim-fade-in-up" style={{ animationDelay: '0s' }}>
            <ProfileHeader showHint={showHint} onCoinClick={handleCoinClick} />
          </div>

          <div className="anim-fade-in-up" style={{ animationDelay: '0.08s' }}>
            <SectionHeader title="Current" />
            <CurrentStatus />
          </div>

          <div id="experience" className="anim-fade-in-up" style={{ animationDelay: '0.16s' }}>
            {renderSection("Experience", "experience", workExperience)}
          </div>

          <div id="education" className="anim-fade-in-up" style={{ animationDelay: '0.24s' }}>
            {renderSection("Education", "education", education)}
          </div>

          <div id="volunteering" className="anim-fade-in-up" style={{ animationDelay: '0.32s' }}>
            {renderSection("Volunteering", "volunteering", volunteering)}
          </div>

          <div className="anim-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <SectionHeader title="Discoveries" />
            <div className="-mx-6 flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-hide sm:-mx-8 sm:px-8">
              {inspirationThemes.map((theme, i) =>
                theme.slug === "apps" ? (
                  <AppsSlotCard key={theme.slug} delay={0.4 + 0.06 * i} />
                ) : (
                  <InspirationCard key={theme.slug} {...theme} delay={0.4 + 0.06 * i} />
                )
              )}
            </div>
          </div>

          <div
            className="mt-8 flex flex-col items-center gap-4 border-t border-border pt-6 anim-fade-in-up"
            style={{ animationDelay: '0.58s' }}
          >
            <a
              href="mailto:jacobhaas03@gmail.com"
              className="rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              Lets connect
            </a>
            <div className="flex items-center gap-4">
              <a href="https://www.linkedin.com/in/jacob-haas-78710a252" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground transition-colors hover:text-foreground">
                LinkedIn
              </a>
              <span className="text-muted-foreground">·</span>
              <Link to="/imprint" className="text-xs text-muted-foreground transition-colors hover:text-foreground">
                Imprint
              </Link>
            </div>
          </div>
        </div>

        {/* Transition overlay - inside the card */}
        {transition && (
          <CoinTransition
            coinStartX={transition.coinX}
            coinStartY={transition.coinY}
            cardWidth={transition.cardWidth}
            cardHeight={transition.cardHeight}
            portraitAspect={portraitAspect}
            onBack={handleBack}
          />
        )}
      </div>
    </main>
  );
};

export default Index;
